import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {useAuth} from "../context/AuthContext";
import {getFromApi} from "../api/auth";

function formatBytes(bytes) {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatRow({icon, label, value, color = "#6366f1"}) {
    return (
        <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
            <div className="d-flex align-items-center gap-2">
                <i className={`bi bi-${icon}`} style={{color, width: 18}}/>
                <span className="small text-muted">{label}</span>
            </div>
            <span className="fw-semibold">{value}</span>
        </div>
    );
}

export default function User() {
    const {user, logout} = useAuth();
    const [storage, setStorage] = useState(null);

    useEffect(() => {
        getFromApi("api/storage/stats")
            .then(res => setStorage(res.data || null))
            .catch(() => {});
    }, []);

    if (!user) return <div className="py-4">Not logged in.</div>;

    const initial = user.email?.[0]?.toUpperCase() || "U";

    const formatDate = (iso) => {
        if (!iso) return "—";
        return new Date(iso).toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
    };

    return (
        <div>
            <div className="page-header">
                <h4>Profile</h4>
            </div>

            <div className="row g-4">
                {/* Left — avatar card */}
                <div className="col-md-4">
                    <div className="panel text-center">
                        <div className="user-avatar mx-auto mb-3" style={{width: 64, height: 64, fontSize: "1.5rem"}}>
                            {initial}
                        </div>
                        <div className="fw-semibold">{user.email}</div>
                        <div className="text-muted small mt-1 text-capitalize">{user.role || "user"}</div>
                        <hr/>
                        <button onClick={logout} className="btn btn-outline-danger btn-sm w-100">
                            <i className="bi bi-box-arrow-right me-1"/>Sign out
                        </button>
                    </div>
                </div>

                {/* Right — details + storage */}
                <div className="col-md-8 d-flex flex-column gap-4">
                    <div className="panel">
                        <h6 className="fw-semibold mb-3">Account details</h6>
                        <div className="row g-3">
                            <div className="col-sm-6">
                                <label className="form-label small text-muted">Email</label>
                                <div className="fw-medium">{user.email}</div>
                            </div>
                            <div className="col-sm-6">
                                <label className="form-label small text-muted">Role</label>
                                <div className="fw-medium text-capitalize">{user.role || "user"}</div>
                            </div>
                        </div>
                    </div>

                    {/* Storage panel */}
                    <div className="panel">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="fw-semibold mb-0">
                                <i className="bi bi-folder me-2" style={{color: "#8b5cf6"}}/>Storage
                            </h6>
                            <Link to="/storage" className="btn btn-sm btn-outline-secondary">Open</Link>
                        </div>

                        {storage ? (
                            <>
                                <StatRow icon="folder-fill"  label="Total folders"   value={storage.totalFolders} color="#8b5cf6"/>
                                <StatRow icon="folder2"      label="Root folders"    value={storage.rootFolders}  color="#8b5cf6"/>
                                <StatRow icon="folder2-open" label="Subfolders"      value={storage.subFolders}   color="#a78bfa"/>
                                <StatRow icon="file-earmark" label="Files uploaded"  value={storage.totalFiles || 0} color="#6366f1"/>
                                <StatRow icon="hdd"          label="Total size"      value={formatBytes(storage.totalSize || 0)} color="#0ea5e9"/>

                                {storage.recent?.length > 0 && (
                                    <div className="mt-3">
                                        <div className="small fw-semibold text-muted text-uppercase mb-2" style={{letterSpacing: "0.05em"}}>
                                            Recently added
                                        </div>
                                        {storage.recent.map(f => (
                                            <div key={f._id} className="d-flex align-items-center justify-content-between py-1">
                                                <div className="d-flex align-items-center gap-2">
                                                    <i className="bi bi-folder-fill small" style={{color: "#8b5cf6"}}/>
                                                    <Link to={`/storage/${f._id}`} className="small text-decoration-none fw-medium">{f.name}</Link>
                                                </div>
                                                <span className="small text-muted">{formatDate(f.created_on)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-muted small">Loading storage stats...</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
